import api from '@/common/api';
import apiAI from '@/common/api-ai';
import { waitFor, sleep, createUUID } from '@/common/util.js';
import Vue from 'vue';
/* eslint-disable */

// Локальное ожидание ответа: через 2 минуты снимаем индикатор, но не списываем запрос и не считаем это ошибкой провайдера
const WAIT_TIMEOUT = 2 * 60 * 1000;
const TIMEOUT_TEXT = 'AI-эксперт не отвечает, попробуйте сократить вопрос или переформулировать его';
// Ответ с ошибкой, но без текста: пользователю всё равно нужно объяснение
const ERROR_FALLBACK_TEXT = 'Не удалось получить ответ AI-эксперта. Попробуйте отправить вопрос ещё раз.';
// В реестре держим не больше стольких запросов: старые завершённые вытесняем
const MAX_REGISTRY = 200;

// Таймеры живут только в памяти: в sessionStorage их не кладём
const timers = {};

// Реестр запросов по UUID вопроса переживает переподключение и перезагрузку страницы (в рамках вкладки)
const storageKey = (userId) => `ai-expert-requests:${userId}`;
function readRegistry(userId) {
  try {
    const raw = sessionStorage.getItem(storageKey(userId));
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (e) {
    return {};
  }
}
function writeRegistry(userId, requests) {
  try {
    const ids = Object.keys(requests);
    const toStore = {};
    ids.slice(-MAX_REGISTRY).forEach((id) => {
      const req = requests[id];
      // Текст вопроса нужен только для повтора списания: после завершения его не храним
      const finished = req.billing === 'settled' || req.state === 'error' || req.state === 'not_sent';
      toStore[id] = finished ? { ...req, prompt: '' } : req;
    });
    sessionStorage.setItem(storageKey(userId), JSON.stringify(toStore));
  } catch (e) {
    // Хранилище недоступно: работаем только в памяти
  }
}

// Можно ли отправить вопрос: «allowed» описывает доступность абонемента, остаток проверяем отдельно
export function canSendQuestion(summary) {
  if (!summary || !summary.allowed) return false;
  return !!summary.unlimited_requests || Number(summary.remaining_requests) > 0;
}

const initialState = () => ({
  enabled: true,
  // Сводка абонемента: GET /ai-expert/usage/summary/
  summary: null,
  summaryError: false,
  // Запросы по UUID: { id, chatId, prompt, state, billing }
  // state: pending | success | error | timed_out | not_sent; billing: not_started | in_flight | settled | needs_reconcile
  requests: {},
  requestsLoadedFor: null,

  ws: null,
  connected: false,
  pingTimer: null,
  chats: [],
  chat: null,
  chat_id: 0,
  cancelSendingFiles: false,
  currentAbortController: null,
  loading: {
    load: false,loadChat: false,
    addUserMsg: false,
    aiThinks: false,
    pdf: false,
    rename: false,
    delete: false,
  },
  errors: {
    load: [],
    addUserMsg: [],
    aiThinks: [],
  },
  strMessageDisabled: '',
});


export default {
  namespaced: true,
  state: initialState,
  getters: {
    enabled: (state) => state.enabled,
    summary: (state) => state.summary,
    summaryError: (state) => state.summaryError,
    loading: (state) => state.loading,
    connected: (state) => state.connected && state.ws && state.ws.readyState === WebSocket.OPEN,
    chat: (state) => state.chat,
    curChatId: (state) => state.chat_id,
    userIdHash: (state, getters, rootState, rootGetters) => {
      const user = rootGetters['auth/user']
      return user.user.toString()
      // const SECRET = 0x5f3759df;
      // const xored = user.id ^ SECRET
      // const checksum = (xored & 0xFF).toString(16).padStart(2, '0')
      // const payload = xored.toString(36)
      // return `${payload}-${checksum}`
      // DECODE
      // const [payload, checksum] = encoded.split('-')
      // if (!payload || !checksum) return null
      // const xored = parseInt(payload, 36)
      // const expectedChecksum = (xored & 0xFF).toString(16).padStart(2, '0')
      // if (checksum !== expectedChecksum) return null
      // return xored ^ SECRET
    },
    lastMsgThinking: (state) => state.chat?.findLast(el => el.author === 'ai' && el.loading),
    strMessageDisabled: (state) => state.strMessageDisabled,
  },
  mutations: {
    startLoading(state, field) {
      state.loading[field] = true;
    },
    startLoadingId(state, { field, id }) {
      state.loading[field] = id;
    },
    endLoading(state, field) {
      state.loading[field] = false;
    },
    clearError(state) {
      state.errors = initialState().errors;
    },
    setError(state, {type, errors}) {
      state.errors[type] = errors;
    },

    setAIEnabled(state, enabled) {
      state.enabled = enabled;
    },
    setConnected(state, connected) {
      state.connected = connected
    },
    setSocket(state, ws) {
      state.ws = ws
    },
    resetState(state) {
      const initial = initialState()
      Object.keys(initial).forEach((key) => { state[key] = initial[key] })
    },
    setSummary(state, summary) {
      state.summary = summary
      state.summaryError = false
    },
    setSummaryError(state, failed) {
      state.summaryError = failed
    },
    setRequests(state, { userId, requests }) {
      state.requests = requests
      state.requestsLoadedFor = userId
    },
    // Новые ключи в объекте Vue 2 добавляем через Vue.set
    rememberRequest(state, request) {
      Vue.set(state.requests, request.id, request)
    },
    patchRequest(state, { id, ...fields }) {
      const request = state.requests[id]
      if (!request) return
      Object.keys(fields).forEach((key) => Vue.set(request, key, fields[key]))
    },

    // Простое локальное сообщение (например, «не доставлено»)
    addMessage(state, msg) {
      if (!state.chat) state.chat = []
      state.chat.push(msg)
    },
    // Ожидание ответа: добавляется до отправки вопроса
    addWaitingMessage(state, msg) {
      if (!state.chat) state.chat = []
      state.chat.push(msg)
    },
    // История: добавляем по _id, не заменяя весь список
    upsertHistoryById(state, msg) {
      if (!state.chat) state.chat = []
      let found = msg._id ? state.chat.find((el) => el._id === msg._id) : null
      // Ответ мог прийти уже после обрыва соединения: он заменяет локальное ожидание того же вопроса (списания это не запускает)
      if (!found && msg.author === 'ai' && msg.client_request_id) {
        found = state.chat.find((el) => el.author === 'ai' && !el._id
          && el.client_request_id === msg.client_request_id && String(el.chat_id) === String(msg.chat_id))
      }
      if (found) Object.assign(found, msg)
      else state.chat.push(msg)
    },
    // Эхо вопроса пользователя: тот же UUID, что у ожидания, поэтому ожидание ответа им не заменяем
    upsertEchoById(state, msg) {
      if (!state.chat) state.chat = []
      const found = msg._id ? state.chat.find((el) => el._id === msg._id) : null
      if (found) {
        Object.assign(found, msg)
        return
      }
      // Эхо приходит позже ожидания: ставим вопрос перед ним
      const waitingInd = msg.client_request_id
        ? state.chat.findIndex((el) => el.author === 'ai' && el.client_request_id === msg.client_request_id && String(el.chat_id) === String(msg.chat_id))
        : -1
      if (waitingInd >= 0) state.chat.splice(waitingInd, 0, msg)
      else state.chat.push(msg)
    },
    // Ответ бота: сначала запись по _id; затем локальное ожидание того же вопроса в том же чате; иначе добавляем
    upsertBotByIdOrRequest(state, msg) {
      if (!state.chat) state.chat = []
      let target = msg._id ? state.chat.find((el) => el._id === msg._id) : null
      if (!target && msg.client_request_id) {
        target = state.chat.find((el) => el.author === 'ai' && !el._id
          && el.client_request_id === msg.client_request_id && String(el.chat_id) === String(msg.chat_id))
      }
      if (target) Object.assign(target, msg)
      else state.chat.push(msg)
    },
    // Локальное завершение ожидания (таймаут, сбой отправки): индикатор снимается только у этого вопроса
    finishWaitingWithLocalError(state, { id, text }) {
      if (!state.chat) return
      const waiting = state.chat.find((el) => el.author === 'ai' && !el._id && el.client_request_id === id)
      if (!waiting) return
      Vue.set(waiting, 'loading', false)
      Vue.set(waiting, 'error', text)
    },
    setChats(state, items) {
      state.chats = items
    },
    newChat(state, chat) {
      state.chats.unshift(chat)
    },
    setChatName(state, {id, title}) {
      const item = state.chats.find(el=> el.chat_id === id)
      if (item)
        item.title = title
    },
    deleteChat(state, id) {
      if (id === state.chat_id) {
        state.chat = []
        state.chat_id = 0
      }
      state.chats = state.chats.filter(el=> el.chat_id !== id)
    },
    setChat(state, items) {
      state.chat = items
    },
    setChatId(state, id) {
      state.chat_id = id
    },
    setEnabled(state, enabled) {
      state.enabled = enabled
    },

    setCancelSendingFiles(state, value) {
      state.cancelSendingFiles = value
      if (value) {
        const controller = state.currentAbortController
        if (controller) {
          controller.abort()
        }
      }
    },
    setCurrentAbortController(state, controller) {
      state.currentAbortController = controller
    },
    setStrMessageDisabled(state, str) {
      state.strMessageDisabled = str
    }
  },
  actions: {
    async loadEnabled({ commit, state, getters, rootGetters }) {
      commit('startLoading', 'load');
      try {
        const dt = await api.get(`/ai-expert/usage/summary/`, { timeout: 0 })
        commit('setSummary', dt);
        commit('setEnabled', canSendQuestion(dt));
        if (dt && !canSendQuestion(dt)) commit('setStrMessageDisabled', dt.reason || '');
      } catch (err) {
          console.log(err);
          commit('setSummaryError', true);
          commit('setError', { type: 'load', errors: err.data });
          Vue.prototype.$toast.error("Не удалось соединиться с AI-сервером", '', { position: 'topRight' });
          commit('setAIEnabled', false);

      } finally {
        commit('endLoading', 'load');
      }
    },

    async loadChats({ commit, state, getters, dispatch, rootGetters }) {
      dispatch('restoreRequests');
      dispatch('loadEnabled');
      commit('startLoading', 'load');
      try {
        const chats = await apiAI.get(`/chats/${getters.userIdHash}`)
        chats.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))

        commit('setChats', chats);
      } catch (err) {
          console.log(err);
          commit('setError', { type: 'load', errors: err.data });
          Vue.prototype.$toast.error("Не удалось соединиться с AI-сервером", '', { position: 'topRight' });
          commit('setAIEnabled', false);

      } finally {
        commit('endLoading', 'load');
      }
    },

    async newChat({ commit, dispatch, state, getters, rootGetters }) {
      commit('startLoading', 'loadChat');

      try {
        const chat = await apiAI.post(`/chats`, {
          user_id: getters.userIdHash,
          title: 'Новый чат ' + new Date().toLocaleString()
        })
        if (chat) {
          commit('newChat', chat);
          commit('setChat', []);
          await dispatch('connect', chat.chat_id)
        }

      } catch (err) {
          console.log(err);
          commit('setError', { type: 'loadChat', errors: err.data });
      } finally {
        commit('endLoading', 'loadChat');
      }
    },

    async loadChat({ commit, dispatch, state, getters, rootGetters }, id) {
      commit('startLoading', 'loadChat');

      try {
        commit('setChat', []);
        await dispatch('connect', id)
      } catch (err) {
          console.log(err);
          commit('setError', { type: 'loadChat', errors: err.data });
      } finally {
        commit('endLoading', 'loadChat');
      }
    },

    async setChatName({ commit, state, getters, rootGetters }, {id, title}) {
      commit('startLoading', 'rename');
      try {
        const chats = await apiAI.put(`/chats/${getters.userIdHash}/${id}/title`, { title })
        commit('setChatName', { id, title });
      } catch (err) {
          console.log(err);
          commit('setError', { type: 'rename', errors: err.data });
      } finally {
        commit('endLoading', 'rename');
      }
    },

    async deleteChat({ commit, state, getters, rootGetters }, id) {
      commit('startLoading', 'delete');
      try {
        const response = await apiAI.delete(`/chats/${getters.userIdHash}/${id}`, )
        commit('deleteChat', id);
      } catch (err) {
          console.log(err);
          commit('setError', { type: 'delete', errors: err.data });
      } finally {
        commit('endLoading', 'delete');
      }
    },

    async chatToPdf({ state, commit, dispatch, getters }) {
      commit('startLoading', 'pdf');

      try {
        const response = await api.post(`/ai-expert/export-chat-to-pdf/`, {
          report_type: "ai_chat",
          logo: true,
          items: state.chat
        }, { timeout: 0 })
        return response
      } catch (err) {
          console.log(err);
          commit('setError', { type: 'pdf', errors: err.data });
      } finally {
        commit('endLoading', 'pdf');
      }
    },

    async addUserMessage({ state, commit, dispatch, getters }, text) {
      if (!state.connected && state.chat_id) {
        await dispatch('connect', state.chat_id)
      }

      dispatch('restoreRequests')
      // Абонемент проверяем до отправки; списание — только после успешного ответа
      if (!(await dispatch('checkCanSend'))) {
        return false;
      }

      if (!state.chat) {
        await dispatch('newChat')
        await waitFor(() => state.connected)
      }

      if (state.chat.length === 0) {
        const title = text.length > 80 ? text.substring(0, 80) + '...' : text
        await dispatch('setChatName', { id: state.chat_id, title })
      }

      const sendedOk = await dispatch('sendFiles')
      if (!sendedOk)
        return false
      if (!getters.connected) {
        commit('addMessage', { author: 'user', text, error: "Сообщение не доставлено, нет связи с AI-агентом" });
        return true
      }

      // UUID и ожидание создаём до ws.send: быстрый ответ должен найти уже сохранённый запрос
      const id = createUUID()
      const chatId = state.chat_id
      commit('rememberRequest', { id, chatId, prompt: text, state: 'pending', billing: 'not_started' })
      dispatch('persistRequests')
      commit('addWaitingMessage', { author: 'ai', chat_id: chatId, client_request_id: id, text: '', loading: true, error: '' })
      timers[id] = setTimeout(() => {
        delete timers[id]
        const req = state.requests[id]
        if (!req || req.state !== 'pending') return
        // Задержка — не ошибка провайдера: запрос не списываем, поздний успех всё ещё может его завершить
        commit('patchRequest', { id, state: 'timed_out' })
        commit('finishWaitingWithLocalError', { id, text: TIMEOUT_TEXT })
        dispatch('persistRequests')
      }, WAIT_TIMEOUT)
      try {
        state.ws.send(JSON.stringify({ content: text, client_request_id: id }))
      } catch (e) {
        console.log(e)
        clearTimeout(timers[id])
        delete timers[id]
        commit('patchRequest', { id, state: 'not_sent' })
        commit('finishWaitingWithLocalError', { id, text: 'Сообщение не отправлено, проверьте соединение и повторите' })
        dispatch('persistRequests')
        return false
      }
      await sleep(1)
      return true
    },

    // Проверка абонемента перед отправкой: сбой проверки или нулевой остаток не пропускают вопрос
    async checkCanSend({ commit }) {
      try {
        const summary = await api.get(`/ai-expert/usage/summary/`, { timeout: 0 })
        commit('setSummary', summary)
        if (canSendQuestion(summary)) {
          commit('setEnabled', true)
          return true
        }
        const reason = (summary && summary.reason) || 'Запросы к AI-эксперту закончились или абонемент недоступен'
        commit('setEnabled', false)
        commit('setStrMessageDisabled', reason)
        Vue.prototype.$toast.error(reason, '', { position: 'topRight' })
        return false
      } catch (err) {
        console.log(err)
        commit('setSummaryError', true)
        Vue.prototype.$toast.error('Не удалось проверить абонемент, вопрос не отправлен', '', { position: 'topRight' })
        return false
      }
    },

    // Реестр запросов текущего пользователя из sessionStorage (один раз на пользователя)
    restoreRequests({ commit, dispatch, state, getters }) {
      const userId = getters.userIdHash
      if (state.requestsLoadedFor === userId) return
      const requests = readRegistry(userId)
      // Запрос, который был «в процессе списания» при перезагрузке, нужно сверить, а не повторять вслепую
      Object.keys(requests).forEach((id) => {
        if (requests[id] && requests[id].billing === 'in_flight') requests[id].billing = 'needs_reconcile'
      })
      commit('setRequests', { userId, requests })
      dispatch('reconcileRequests')
    },
    // Подтверждённый успех, у которого списание не прошло: повторяем тот же POST с тем же ключом, но не больше трёх раз
    reconcileRequests({ commit, dispatch, state }) {
      Object.keys(state.requests).forEach((id) => {
        const req = state.requests[id]
        if (req.state === 'success' && req.billing === 'needs_reconcile' && (req.attempts || 0) < 3) {
          commit('patchRequest', { id, billing: 'not_started', attempts: (req.attempts || 0) + 1 })
          dispatch('consumeSuccessfulRequest', id)
        }
      })
    },
    persistRequests({ state, getters }) {
      writeRegistry(getters.userIdHash, state.requests)
    },
    // Выход из аккаунта: реестр пользователя и таймеры очищаем
    clearRequests({ commit, state, getters }) {
      Object.keys(timers).forEach((id) => { clearTimeout(timers[id]); delete timers[id] })
      try {
        sessionStorage.removeItem(storageKey(getters.userIdHash))
      } catch (e) {}
      try {
        if (state.ws) state.ws.close()
      } catch (e) {}
      // Данные прежнего пользователя (чаты, сводка, соединение) не должны остаться следующему
      commit('resetState')
    },

    async sendFiles({ state, commit, dispatch, getters, rootGetters }) {
      const pendingFiles = rootGetters['ai/dossier/pendingFiles']

      for (let i=0 ; i<pendingFiles.length ; i++) {
        commit('ai/dossier/setPendingFileInfo', {ind: i, sending: true }, { root: true })
        const fileItem = pendingFiles[i]

        if (state.cancelSendingFiles) {
          console.log('Загрузка отменена пользователем')
          break
        }
        const controller = new AbortController()
        commit('setCurrentAbortController', controller)

        try {
          let content = ''
          if (fileItem.fromDossier) {
            const response = await api.get(`/dossier/${fileItem.id}/extract-text/`, { signal: controller.signal, timeout: 0 })
            if (response)
              content = response.text
              // console.log('dossier', fileItem, content)
          } else {
              const form = new FormData()
              form.append('file', fileItem.file)
              const response = await api.post( `/ai-expert/extract-text/`, form, { signal: controller.signal, timeout: 0 })
              content = response.text
          }

          commit('ai/dossier/setPendingFileInfo', {ind: i, sending: false, sended: true, content }, { root: true })
        } catch (e) {
          commit('ai/dossier/setPendingFileInfo', {ind: i, sending: false, sended: false, error: true }, { root: true })
          console.log(e)
        }
      }

      if (!state.cancelSendingFiles) {
        for (let i=0 ; i<pendingFiles.length ; i++) {
          const fileItem = pendingFiles[i]
          if (fileItem.sended)
            state.ws.send(JSON.stringify({ filename: fileItem.name, content: fileItem.content }))
        }
      }

      // commit('ai/dossier/setPendingFiles', [], { root: true })
      const cancel = state.cancelSendingFiles
      commit('setCancelSendingFiles', false)
      commit('setCurrentAbortController', null)
      return !cancel
    },

    connect({ commit, dispatch, state, getters, rootGetters }, chat_id) {
      if (state.ws) {
        state.ws.close()
      }
      if (!chat_id) {
        console.error('chat_id empty')
        return
      }

      try {
        let host = process.env.VUE_APP_BASE_URL_AI_WS
        host = host.endsWith("/") ? host.slice(0, -1) : host;

        const socket = new WebSocket(`${host}/chat/${getters.userIdHash}/${chat_id}`)

        socket.addEventListener("open", () => {
          console.log("WebSocket connected")
          commit('setConnected', true)
          commit('setChatId', chat_id);
          socket.send(JSON.stringify({ type: "get_history", chat_id }))
          state.pingTimer = setInterval(()=>{
            socket.send(JSON.stringify({ type: "ping" }))
          }, 60*1000)
        })

        socket.addEventListener("message", (event) => {
            const data = JSON.parse(event.data)
            // console.log('message', data)
            // Сохраняем поля протокола: старые записи могут не иметь status, их не считаем успехом
            function convertMsg(src) {
              return {
                _id: src._id,
                chat_id: src.chat_id,
                client_request_id: src.client_request_id,
                status: src.status,
                error_code: src.error_code,
                sender_id: src.sender_id,
                user_id: src.user_id,
                author: src.sender_id === getters.userIdHash ? 'user' : 'ai',
                text: src.content,
                timestamp: src.timestamp,
                filename: src.filename,
                loading: false,
                error: src.status === 'error' ? (src.content || ERROR_FALLBACK_TEXT) : ''
              }
            }

            // История только восстанавливает экран: ни одна её ветка не списывает запрос
            if (Array.isArray(data) || Array.isArray(data.messages)) {
              const items = Array.isArray(data) ? data : data.messages
              items.forEach((src) => commit('upsertHistoryById', convertMsg(src)))
              return
            }
            if (data.type === 'history') {
              commit('upsertHistoryById', convertMsg(data))
              return
            }
            if (data.type !== 'message') return

            const msg = convertMsg(data)
            // Эхо вопроса и файлы: показываем, но ожидание ответа не трогаем и оплату не запускаем
            if (data.sender_id !== 'bot' || data.filename) {
              commit('upsertEchoById', msg)
              return
            }
            commit('upsertBotByIdOrRequest', msg)

            const req = state.requests[data.client_request_id]
            // Нет своего ожидающего запроса (старый клиент, чужой чат, потеря данных): ответ показан без списания
            if (!req || String(req.chatId) !== String(data.chat_id)) return
            if (data.status !== 'success' && data.status !== 'error') return
            // Завершить можно только ожидающий или задержанный запрос: повтор доставки ничего не делает
            if (req.state !== 'pending' && req.state !== 'timed_out') return

            clearTimeout(timers[req.id])
            delete timers[req.id]
            commit('patchRequest', { id: req.id, state: data.status })
            dispatch('persistRequests')
            if (data.status === 'success' && typeof data.content === 'string' && data.content.trim()) {
              dispatch('consumeSuccessfulRequest', req.id)
            }
        })

        socket.addEventListener("close", (event) => {
          commit('setConnected', false)
          clearInterval(state.pingTimer)
          state.pingTimer = 0
          console.log('WS closed')
        })

        commit('setSocket', socket)
      }
      catch(e) {
        console.log(e)
      }
    },

    // Списание одного запроса за подтверждённый успех. Ключ — UUID вопроса: повтор с ним не создаёт второго списания
    async consumeSuccessfulRequest({ commit, dispatch, state }, id) {
      const req = state.requests[id]
      if (!req || req.state !== 'success') return
      if (req.billing !== 'not_started') return
      // Защита от параллельных списаний ставится до HTTP-запроса
      commit('patchRequest', { id, billing: 'in_flight' })
      dispatch('persistRequests')
      commit('startLoading', 'addUserMsg')
      try {
        const response = await api.post(`/ai-expert/usage/consume/`, {
          amount: 1,
          idempotency_key: req.id,
          prompt_text: req.prompt
        }, { timeout: 0 })
        commit('patchRequest', { id, billing: 'settled' })
        dispatch('persistRequests')
        if (response && typeof response === 'object' && 'allowed' in response) {
          commit('setSummary', response)
          commit('setEnabled', canSendQuestion(response))
        } else {
          dispatch('loadEnabled')
        }
      } catch (err) {
        console.log(err)
        // Ответ остаётся на экране; ключ сохраняем для сверки, нового POST не делаем
        commit('patchRequest', { id, billing: 'needs_reconcile' })
        dispatch('persistRequests')
        dispatch('loadEnabled')
      } finally {
        commit('endLoading', 'addUserMsg')
      }
    }

  },
};
